// Offline action queue
// ─────────────────────
// When the device is offline, mutating fetches (POST / PUT / DELETE / PATCH)
// would normally fail and the user has to remember to retry. Instead we
// serialize the request into localStorage and replay it the moment the
// browser fires "online".
//
// Caveats / safety
// ────────────────
// * Only fetches against the same origin (i.e. our backend) are queued.
// * Auth-sensitive paths are skipped — login/logout/password reset must
//   always reach the server immediately.
// * We never queue file uploads (FormData / Blob bodies) because we'd lose
//   the binary on a page reload. Those still fail-fast.
// * Each entry has a 7-day TTL so stale queued actions don't replay against
//   a server that's moved on.

const STORAGE_KEY = 'kk-offline-queue';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const NEVER_QUEUE = [
  '/api/auth/login',
  '/api/auth/logout',
  '/api/auth/register',
  '/api/auth/reset',
  '/api/presence',
];

const safeRead = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};
const safeWrite = (q) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(q)); }
  catch { /* quota — drop silently */ }
};
const subs = new Set();
const notify = () => { const q = safeRead(); subs.forEach((cb) => { try { cb(q); } catch {} }); };

export const subscribeOfflineQueue = (cb) => {
  subs.add(cb);
  cb(safeRead());
  return () => subs.delete(cb);
};

export const getQueueSize = () => safeRead().length;

export const enqueue = (entry) => {
  const q = safeRead();
  q.push({ ...entry, id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, queued_at: Date.now() });
  safeWrite(q);
  notify();
};

const removeById = (id) => {
  const q = safeRead().filter((x) => x.id !== id);
  safeWrite(q);
  notify();
};

const purgeExpired = () => {
  const cutoff = Date.now() - TTL_MS;
  const q = safeRead().filter((x) => x.queued_at > cutoff);
  safeWrite(q);
};

/**
 * Decide whether a given mutation is queueable.
 * Returns false for GETs (already cached by SW), file uploads, and the
 * never-queue auth/presence paths.
 */
export const shouldQueue = (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') return false;
  if (NEVER_QUEUE.some((p) => url.includes(p))) return false;
  // FormData / Blob bodies aren't safely round-trippable through JSON.
  const body = options.body;
  if (body && typeof body !== 'string') return false;
  return true;
};

/**
 * Replay a single queued entry. Returns true on success, false otherwise.
 * Authorization header is rebuilt from current token in case the user
 * re-authenticated between offline+online.
 */
const replayOne = async (entry) => {
  const tok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  try {
    const r = await fetch(entry.url, {
      method: entry.method,
      headers: {
        'Content-Type': 'application/json',
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      },
      body: entry.body,
    });
    return r.ok;
  } catch {
    return false;
  }
};

/**
 * Drain the queue serially and return the count of successes / failures.
 * The toast helper is injected so this module stays UI-framework-free.
 */
export const drainQueue = async (toast) => {
  purgeExpired();
  let q = safeRead();
  if (!q.length) return { ok: 0, failed: 0 };
  let ok = 0, failed = 0;
  for (const entry of [...q]) {
    const success = await replayOne(entry);
    if (success) {
      removeById(entry.id);
      ok++;
      if (toast) toast.success(`Synced: ${entry.label || `${entry.method} ${entry.url.split('/api')[1] || ''}`}`);
    } else {
      failed++;
      // leave it in the queue; will retry on next online event
    }
  }
  return { ok, failed };
};

// Auto-replay when the browser reports online connectivity.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    // dynamic import to avoid pulling sonner into the SW build path
    import('sonner').then(({ toast }) => drainQueue(toast)).catch(() => drainQueue(null));
  });
}
